// @ts-nocheck

import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { ModalDimension, ModalFormat, ModalSpacing } from '../../constants/ui/modal';
import { ZIndices } from '../../styled-components/dark/common';
import { Modal as ModalTheme } from '../../styled-components/dark/modal';
import { UserNotificationCtaWrapper } from '../../styled-components/shared';
import ScrollView from '../ScrollView';

export const VerticalCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  transform: translate3d(0, 0, 0);

  ${({ $hideOverlay }) => {
    if ($hideOverlay) {
      return css`
        width: fit-content;
        height: fit-content;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      `;
    }
  }}
`;

export const DesktopWrapper = styled.div`
  position: relative;
  height: ${({ $fullHeight }) => ($fullHeight ? 'calc(100% - 6em)' : `${ModalDimension.DESKTOP.height}px`)};
  max-height: ${ModalDimension.DESKTOP.maxHeight}px;
  width: ${ModalDimension.DESKTOP.width}px;
  max-width: ${ModalDimension.DESKTOP.maxWidth}px;

  ${({ $format }) =>
    $format === `${ModalFormat.XSMALL}`
      ? css`
          max-width: ${ModalDimension.XSMALL.maxWidth}px;
          max-height: ${ModalDimension.XSMALL.maxHeight}px;

          ${Target.__MOBILE__
            ? css`
                @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
                  max-height: ${ModalDimension.TABLET.maxHeight}px;
                }
              `
            : undefined}

          width: 100%;
        `
      : undefined}

  ${({ $format, $viewportWidth }) =>
    $format === `${ModalFormat.SMALL}`
      ? css`
          height: 100%;
          max-height: ${ModalDimension.SMALL.maxHeight}px;
          width: calc(${$viewportWidth}px - ${ModalSpacing.PADDING}px);
        `
      : undefined}

  ${({ $withBorder }) => {
    if ($withBorder) {
      return css`
        &:after {
          content: '';
          position: absolute;
          width: calc(100% + 2em);
          height: calc(100% + 2em);
          top: -1em;
          left: -1em;
          background: #121213;

          ${({ $zIndex }) => css`
            z-index: ${$zIndex - 1};
          `}
        }
      `;
    }
  }}

  ${({ $forcedHeight }) =>
    typeof $forcedHeight === 'number'
      ? css`
          height: ${$forcedHeight}px;
        `
      : undefined}
`;
export const Wrapper = styled.div`
  background: ${({ $backgroundColor }) => $backgroundColor || ModalTheme.backgroundColor.fallback};
  z-index: ${({ $zIndex }) => $zIndex};
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
  overflow: ${({ $noOverflowHidden }) => ($noOverflowHidden ? undefined : 'hidden')};
  position: absolute;

  h2 {
    margin-top: 0;
  }

  h3 {
    color: ${ModalTheme.textColor.default};
  }

  ${({ $hasNavBarSpacing }) =>
    $hasNavBarSpacing
      ? css`
          top: 3em;
          top: calc(3em + constant(safe-area-inset-top));
          top: calc(3em + env(safe-area-inset-top));
        `
      : undefined}

  ${({ $format, $hasNavBarSpacing }) =>
    $format === ModalFormat.FULLSCREEN && !$hasNavBarSpacing
      ? css`
          top: constant(safe-area-inset-top);
          top: env(safe-area-inset-top);
        `
      : undefined}

  ${({ $isGrid }) =>
    $isGrid
      ? css`
          font-family: BettingPoliticaBold, sans-serif;
          border-radius: 1em;
        `
      : undefined}

  ${({ $hasBorderRadius }) =>
    $hasBorderRadius
      ? css`
          border-radius: 15px;
        `
      : undefined}

  ${UserNotificationCtaWrapper} {
    background: ${({ $backgroundColor }) => $backgroundColor};
  }
`;

export const SafeAreaWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  left: constant(safe-area-inset-left);
  left: env(safe-area-inset-left);
  right: 0;
  right: constant(safe-area-inset-right);
  right: env(safe-area-inset-right);
  bottom: 0;

  ${({ $format }) =>
    $format === ModalFormat.FULLSCREEN
      ? css`
          bottom: constant(safe-area-inset-bottom);
          bottom: env(safe-area-inset-bottom);
        `
      : undefined}
`;

export const StyledScrollView = styled(ScrollView)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

export const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
`;

export const Footer = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  z-index: 1;
  top: auto;
  bottom: 0;
`;

export const Fixed = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;

  ${({ $fixedStyle }) => css`
    ${$fixedStyle
      ? css`
          ${$fixedStyle}
        `
      : undefined}
  `}
`;

export const Overlay = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  top: 0;
  left: 0;
  transition-property: opacity;
  transition-timing-function: ease-in-out;

  ${({ $overlayBackground, $zIndex = ZIndices.overlay, $isOpen = true, $timingDuration }) => css`
    transition-duration: ${$timingDuration ? `${$timingDuration}ms` : '1250ms'};
    transform: ${$isOpen ? 'translateX(0%)' : 'translateX(-100%)'};
    background: ${$overlayBackground ? $overlayBackground : 'rgba(0, 0, 0, 0.6)'};
    z-index: ${$zIndex};
    opacity: ${$isOpen ? 1 : 0};
  `}
`;

export const ScrollableContent = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  overflow-y: scroll;
  border-radius: 0.4em;
  padding: 0.6em;

  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    pointer-events: auto;
  }
`;
